import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';

import { HOST_GROUPS_LOADED_EVENT } from 'consts';
import { LocalizationContext } from 'localization';
import { emitter } from 'services/EventEmitter';
import { processHostsChanges } from 'utils';

import { useDeleteHostGroup } from '../useDeleteHostGroup';
import {
  GroupsById,
  HostsDataResponse,
  IReturnHostDataValue,
} from './interfaces';
import { HOSTS_DATA } from './schema';

export function useHostsData(): IReturnHostDataValue {
  const { t } = useContext(LocalizationContext);

  const [pollInterval, setPollInterval] = useState(30000);

  useEffect(() => {
    const checkRequestTime = (time: number): void => {
      if (time > 5) {
        setPollInterval(60000);
      }
    };

    emitter.on(HOST_GROUPS_LOADED_EVENT, checkRequestTime);

    return () => {
      emitter.off(HOST_GROUPS_LOADED_EVENT, checkRequestTime);
    };
  }, []);

  const deleteHostGroup = useDeleteHostGroup(t);

  const {
    loading,
    error,
    data,
    previousData,
    refetch,
    stopPolling,
    startPolling,
  } = useQuery<HostsDataResponse>(HOSTS_DATA, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
    pollInterval: pollInterval,
    errorPolicy: 'ignore',
    onError: console.error,
  });

  const groupsById = useMemo(() => {
    const groupsById: GroupsById = {};

    data?.hostGroupsOverview.forEach((group) => {
      groupsById[group.id] = group;
    });

    return groupsById;
  }, [data]);

  const statedPoling = useCallback(
    () => startPolling(pollInterval),
    [pollInterval]
  );

  useEffect(() => {
    if (!data) {
      return;
    }

    const previousHosts = previousData?.hostsOverview;

    if (!previousHosts) return;

    const newHosts = data.hostsOverview;

    if (newHosts.length && previousHosts.length) {
      processHostsChanges(newHosts, previousHosts, t);
    }
  }, [previousData]);

  return {
    groupsById,
    error,
    hosts: data?.hostsOverview ?? [],
    loading,
    refetch,
    deleteHostGroup,
    isFirstLoading: loading && !data,
    stopPolling,
    statedPoling,
  };
}
